import { useContext } from "react";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import { promisifyApi } from "../../../utility/utils";

interface RoleInput {
    name: string;
    permissions: string[];
}

interface UpdateRolePermissionsParams {
    roles: RoleInput[];
}

export const useUpdateRolePermissions = () => {
    const { current: session } = useContext(SessionContext);
    const queryClient = useQueryClient();
    const updateRolePermissionsAPI = promisifyApi(api.hooks.rbac.updateRolePermissions());

    const userUuid = session?.site?.user?.uuid;
    const tenantId = session?.site?.tenant?.id;

    return useMutation({
        mutationFn: async (params: UpdateRolePermissionsParams) => {
            if (!session?.context) {
                throw new Error('Session context not available');
            }
            return await updateRolePermissionsAPI.send(session.context, params);
        },
        onSuccess: (updatedRolesResponse) => {
            // Directly update the cache with the returned data due to brief flash of old data
            if (updatedRolesResponse) {
                queryClient.setQueryData(
                    ['roles', userUuid, tenantId],
                    updatedRolesResponse
                );
            }

            // Invalidate userRoles(permissions) cache so that we can see the changes in the UI without refreshing the page
            queryClient.invalidateQueries({ queryKey: ['userRoles'] });
        },
        onError: (error) => {
            console.error('Failed to update role permissions:', error);
        }
    });
}; 